/**
 * Coaching session status
 */
export type CoachingSessionStatus = "New" | "Active" | "Completed";

/**
 * Redux action types for the coaching module
 */
export enum CoachingActionTypes {
    SET_COACHING_SESSIONS = "SET_COACHING_SESSIONS",
    UPDATE_COACHING_SESSION = "UPDATE_COACHING_SESSION",
    SET_COACHING_COMMENTS = "SET_COACHING_COMMENTS",
    ADD_PENDING_COACHING_COMMENT = "ADD_PENDING_COACHING_COMMENT",
    UPDATE_PENDING_COMMENT_STATE = "UPDATE_PENDING_COMMENT_STATE",
    ENQUEUE_COACHING_COMPLETION = "ENQUEUE_COACHING_COMPLETION",
    UPDATE_COACHING_COMPLETION_STATE = "UPDATE_COACHING_COMPLETION_STATE",
    CLEAR_COACHING_STATE = "CLEAR_COACHING_STATE",
    REASSIGN_PENDING_COMMENTS = "REASSIGN_PENDING_COMMENTS",
    ENQUEUE_NEW_SESSION_RESOLUTION = "ENQUEUE_NEW_SESSION_RESOLUTION",
    SET_NEW_SESSION_RESOLUTION = "SET_NEW_SESSION_RESOLUTION",
    REMOVE_PENDING_SESSION_COMMENTS = "REMOVE_PENDING_SESSION_COMMENTS",
    REMOVE_PENDING_SESSION_COMPLETION = "REMOVE_PENDING_SESSION_COMPLETION"
}

/**
 * Sync state of an offline-authored comment; absent on server-sourced comments.
 * "synced" = POSTed but not yet refetched, so it still renders from the local copy.
 */
export type CoachingCommentSyncStatus = "pending" | "failed" | "synced";

/**
 * Sync state of an offline session-completion operation.
 */
export type CoachingCompletionSyncStatus = "pending" | "failed" | "synced";

/**
 * Offline session-completion operation awaiting sync.
 *
 * When sessionId is NEW_SESSION_KEY (the "new" sentinel), the sync layer must first find
 * an Active session for this user (or create one) before issuing the Set call.
 */
export interface ICoachingPendingCompletion {
    sessionId: string;            // NEW_SESSION_KEY ("new") when the session is still pending creation
    requestedDateTime: string;    // ISO; value written to acknowledgementDateTime on Set
    modified?: "Add";
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _syncCounter?: number;
    syncStatus?: CoachingCompletionSyncStatus;
    failureReason?: string;
}

/**
 * Offline operation to resolve (reuse/create) the driver's NEW session before its
 * queued comments/completion can sync. Owned by the sync layer.
 */
export interface ICoachingPendingSessionResolution {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _syncCounter?: number;
    syncStatus?: CoachingCompletionSyncStatus;
    failureReason?: string;
}

/**
 * Per-user slice of the coaching store
 */
export interface ICoachingStore {
    userId: string;
    sessions: ICoachingSession[];
    comments: IIdDictionary<ICoachingComment[]>;
    pendingCompletions: IIdDictionary<ICoachingPendingCompletion>;
    lastFetchTimestamp: number;
    newSessionResolution?: ICoachingPendingSessionResolution;
}

/**
 * Coaching session entity (mapped from API response)
 */
export interface ICoachingSession {
    id: string;
    status: CoachingSessionStatus;
    modifiedDate?: string;
    assignedDate?: string;
    acknowledgementDateTime?: string;
    eventSummaries: { ruleType: string; count: number }[];
}

/**
 * Raw coaching session from MyGeotab API
 */
export interface IRawCoachingSession {
    id: string;
    state: string;
    createdDateTime?: string;
    modifiedDateTime?: string;
    acknowledgementDateTime?: string;
    exceptionEvents?: IRawExceptionEvent[];
}

/**
 * Raw exception event from MyGeotab API (nested in CoachingSession response).
 * Duration arrives as a .NET TimeSpan string (e.g., "00:05:30.0000000").
 * Distance is in km.
 */
export interface IRawExceptionEvent {
    id?: string;
    rule?: { id?: string; name?: string };
    device?: { id?: string; name?: string };
    activeFrom?: string;
    activeTo?: string;
    distance?: number;
    duration?: string;
}

/**
 * Display-ready speed-change data for a harsh braking/acceleration event.
 * Speeds are pre-formatted in the user's unit (e.g. "80 km/h"); `duration` is a
 * one-decimal seconds string (e.g. "2.0"). `severity` drives the indicator colour.
 */
export interface IExceptionEventSpeedChange {
    startSpeed: string;
    endSpeed: string;
    duration: string;
    severity: "Low" | "Moderate" | "Severe";
}

/**
 * Individual exception event within a coaching session
 */
export interface IExceptionEventSummary {
    id: string;
    ruleType: string;
    assetName: string;
    duration: string;
    distance: string;
    date: string;
    location: string;
    deviceId?: string;
    ruleId?: string;
    activeFrom?: string;
    activeTo?: string;
    speedChange?: IExceptionEventSpeedChange;
}

/**
 * Comment on a coaching session.
 *
 * Offline write markers (`modified`, `_syncCounter`, `syncStatus`, `failureReason`)
 * are present only on locally-authored comments awaiting sync, following the
 * `IHashMap` convention used by the `sync/` layer (e.g. `media.ts`, `dvir.ts`).
 * The comment `id` is a client-generated `uuid()` that the server preserves
 * (`PopulateGuidId`), giving idempotent retries and a stable identity across sync.
 */
export interface ICoachingComment {
    id: string;
    authorName: string;
    dateTime: string;
    comment: string;
    modified?: "Add";
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _syncCounter?: number;
    syncStatus?: CoachingCommentSyncStatus;
    failureReason?: string;
}

/**
 * Raw coaching session comment from MyGeotab API
 */
export interface IRawCoachingSessionComment {
    id: string;
    coachingSessionId: string;
    dateTime: string;
    text: string;
    user?: {
        id?: string;
        firstName?: string;
        lastName?: string;
    };
}

/**
 * Date range for safety driver insight queries
 */
export interface IDateRange {
    from: string;
    to: string;
}

/**
 * Map coordinate (latitude/longitude pair)
 */
export interface IMapCoordinate {
    latitude: number;
    longitude: number;
}

/**
 * Map data for an exception event: a pin marking where it occurred
 * and a polyline tracing the path over its duration.
 */
export interface IExceptionEventMapData {
    pin: IMapCoordinate | null;
    polyline: IMapCoordinate[];
}

/**
 * Request parameters for fetching exception event map coordinates
 */
export interface IExceptionEventLocationRequest {
    deviceId: string;
    activeFrom: string;
    activeTo: string;
}

/**
 * Safety driver insight entity from MyGeotab API
 */
export interface ISafetyDriverInsight {
    id: string;
    driver?: { id?: string };
    observationDate?: string;
    daysDriven?: number;
    predictedCrashes?: number;
    predictedCrashesBenchmark?: number;
    predictedCrashesBestInClass?: number;
    crashProbabilityKm?: number;
    crashProbabilityMile?: number;
    crashProbabilityBenchmarkKm?: number;
    crashProbabilityBenchmarkMile?: number;
    crashProbabilityBestInClassKm?: number;
    crashProbabilityBestInClassMile?: number;
    crashProbabilityRange?: string;
    crashProbabilityRank?: number;
    harshAccelerationRank?: number;
    harshAccelerationRange?: string;
    harshBrakingRank?: number;
    harshBrakingRange?: string;
    harshCorneringRank?: number;
    harshCorneringRange?: string;
    relativeRiskKm?: number;
    relativeRiskMile?: number;
    relativeRiskRange?: string;
    speedingRank?: number;
    speedingRange?: string;
    seatbeltRank?: number;
    overallSafetyRank?: number;
    collisionCount?: number;
    totalDistanceKm7Days?: number;
    totalDurationHrs7Days?: number;
    totalDistanceKmTripWindow?: number;
    totalDurationHrsTripWindow?: number;
    distanceToday?: number;
    durationToday?: number;
    crashProbabilityRiskLabel?: string;
    aggregatedAreaRisk?: number;
    areaRiskClassificationText?: string;
    areaRiskClassificationEnum?: number;
    lastTripId?: number;
    lastTripStartTime?: string;
    lastTripsCount?: number;
    tailgatingRank?: number;
    tailgatingRange?: string;
    tailgatingDistanceKmToday?: number;
    tailgatingDistanceKmTripWindow?: number;
    totalAdasDistanceKmTripWindow?: number;
    adasDistanceTodayKm?: number;
    metaData?: string;
    majorVersion?: number;
    minorVersion?: number;
    patchVersion?: number;
}
